import { useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ImagePlaceholder } from '@components/ui/ImagePlaceholder'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { useLang, withLang } from '@shared/i18n/useLang'

// ── Типы блоков контента ───────────────────────────────────────
export type CaseBlock =
  | { type: 'task'; paragraphs: string[] }
  | { type: 'works'; items: string[] }
  | { type: 'task-and-works'; paragraphs: string[]; items: string[] }
  | { type: 'task-execution'; task: string; execution: string }
  | { type: 'route'; title?: string; steps: { title: string; desc: string; duration: string }[] }
  | { type: 'photo-stages'; title?: string; steps: { title: string; photo?: string }[] }
  | { type: 'photos'; items: { label: string; photo?: string }[] }

export interface CasePageProps {
  category: string
  year: string
  title: string
  subtitle: string
  mainPhoto?: string
  facts: { k: string; v: string }[]
  blocks: CaseBlock[]
  similar: { category: string; year: string; title: string; desc: string }[]
}

// ── Вспомогательные компоненты ─────────────────────────────────
function WorkItem({ n, text }: { n: number; text: string }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #D9DEE2', borderRadius: 3, padding: '18px 20px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <span style={{ flexShrink: 0, width: 26, height: 26, background: '#171C22', color: '#FFFFFF', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{n}</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.55, color: '#3A434D', paddingTop: 3 }}>{text}</span>
    </div>
  )
}

function SectionH2({ children }: { children: React.ReactNode }) {
  return <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{children}</h2>
}

// ── Рендереры блоков ───────────────────────────────────────────

// 2a — только текст задачи (узкая колонка 1/1.6)
function BlockTask({ b }: { b: Extract<CaseBlock, { type: 'task' }> }) {
  const { t } = useTranslation()
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page grid-case-split">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionH2>{t('case.task')}</SectionH2>
          {b.paragraphs.map((p, i) => (
            <p key={i} style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7, color: '#5C6670' }}>{p}</p>
          ))}
        </div>
        <div />
      </div>
    </section>
  )
}

// 2b — только список выполненных работ
function BlockWorks({ b }: { b: Extract<CaseBlock, { type: 'works' }> }) {
  const { t } = useTranslation()
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SectionH2>{t('case.worksDone')}</SectionH2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {b.items.map((text, i) => <WorkItem key={i} n={i + 1} text={text} />)}
        </div>
      </div>
    </section>
  )
}

// Комбинированный (как в кейсе из файла 1): задача 1fr + работы 1.6fr
function BlockTaskAndWorks({ b }: { b: Extract<CaseBlock, { type: 'task-and-works' }> }) {
  const { t } = useTranslation()
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page grid-case-split">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionH2>{t('case.task')}</SectionH2>
          {b.paragraphs.map((p, i) => (
            <p key={i} style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7, color: '#5C6670' }}>{p}</p>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionH2>{t('case.worksDone')}</SectionH2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {b.items.map((text, i) => <WorkItem key={i} n={i + 1} text={text} />)}
          </div>
        </div>
      </div>
    </section>
  )
}

// 1a — задача (тёмная) + исполнение (белая) — равные колонки
function BlockTaskExecution({ b }: { b: Extract<CaseBlock, { type: 'task-execution' }> }) {
  const { t } = useTranslation()
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page grid-2">
        <div style={{ background: '#171C22', borderRadius: 3, padding: 40, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 8, height: 8, background: '#189BD5', flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#93A3AE' }}>{t('case.task')}</span>
          </div>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.7, color: '#E7EBEE' }}>{b.task}</p>
        </div>
        <div style={{ background: '#FFFFFF', border: '1px solid #D9DEE2', borderRadius: 3, padding: 40, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 8, height: 8, background: '#189BD5', flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#5C6670' }}>{t('case.execution')}</span>
          </div>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.7, color: '#3A434D' }}>{b.execution}</p>
        </div>
      </div>
    </section>
  )
}

// 1b — этапы-маршрут с пунктирной линией
function BlockRoute({ b }: { b: Extract<CaseBlock, { type: 'route' }> }) {
  const { t } = useTranslation()
  const n = b.steps.length
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
        <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>
          {b.title ?? t('case.defaultRouteTitle')}
        </h2>
        <div className="grid-4" style={{ rowGap: 40, columnGap: 0 }}>
          {b.steps.map((step, i) => {
            const isLast = i === n - 1
            const isRowEnd = (i + 1) % 4 === 0
            const hideConnector = isLast || isRowEnd
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingRight: hideConnector ? 0 : 28 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    flexShrink: 0, width: 34, height: 34,
                    background: isLast ? '#189BD5' : '#171C22',
                    color: '#FFFFFF', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700,
                  }}>{i + 1}</span>
                  {!hideConnector && (
                    <span style={{
                      flex: 1, height: 2, marginLeft: 10,
                      background: 'repeating-linear-gradient(90deg,#B7C1C9 0 8px,transparent 8px 14px)',
                    }} />
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 650 as number, color: '#171C22' }}>{step.title}</span>
                  <span style={{ fontSize: 13, lineHeight: 1.6, color: '#5C6670' }}>{step.desc}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: '#189BD5' }}>{step.duration}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// 1c — этапы с картинками (4-col карточки)
function BlockPhotoStages({ b }: { b: Extract<CaseBlock, { type: 'photo-stages' }> }) {
  const { t } = useTranslation()
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
        <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>
          {b.title ?? t('case.defaultStagesTitle')}
        </h2>
        <div className="grid-4" style={{ gap: 16 }}>
          {b.steps.map((step, i) => (
            <div key={i} className="card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {step.photo
                ? <img src={step.photo} alt={step.title} loading="lazy" style={{ width: '100%', height: 190, objectFit: 'cover', display: 'block', flexShrink: 0 }} />
                : <ImagePlaceholder height={190} radius={0} />}
              <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexShrink: 0, width: 24, height: 24, background: '#171C22', color: '#FFFFFF', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{i + 1}</span>
                <span style={{ fontSize: 14.5, fontWeight: 650 as number, color: '#171C22' }}>{step.title}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// Фото хода работ (3-col сетка)
function BlockPhotos({ b }: { b: Extract<CaseBlock, { type: 'photos' }> }) {
  return (
    <section style={{ padding: '56px 0 0' }}>
      <div className="container-page">
        <div className="grid-3">
          {b.items.map((item, i) => (
            <div key={i} style={{ border: '1px solid #D9DEE2', borderRadius: 3, overflow: 'hidden' }}>
              {item.photo
                ? <img src={item.photo} alt={item.label} loading="lazy" style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
                : <ImagePlaceholder height={220} radius={0} />}
              {item.label && (
                <div style={{ padding: '10px 14px', fontSize: 12.5, color: '#5C6670' }}>{item.label}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function renderBlock(block: CaseBlock, i: number) {
  switch (block.type) {
    case 'task':             return <BlockTask key={i} b={block} />
    case 'works':            return <BlockWorks key={i} b={block} />
    case 'task-and-works':   return <BlockTaskAndWorks key={i} b={block} />
    case 'task-execution':   return <BlockTaskExecution key={i} b={block} />
    case 'route':            return <BlockRoute key={i} b={block} />
    case 'photo-stages':     return <BlockPhotoStages key={i} b={block} />
    case 'photos':           return <BlockPhotos key={i} b={block} />
  }
}

// ── Главный компонент ──────────────────────────────────────────
export function CasePage({ category, year, title, subtitle, mainPhoto, facts, blocks, similar }: CasePageProps) {
  const { t } = useTranslation()
  const lang = useLang()
  const { pathname } = useLocation()
  const railRef = useRef<HTMLDivElement>(null)
  const scrollRail = (dir: 1 | -1) => railRef.current?.scrollBy({ left: dir * 352, behavior: 'smooth' })

  return (
    <>
      <Seo
        title={title}
        description={subtitle || t('seo.raboty.description')}
        image={mainPhoto}
        breadcrumbs={[
          { name: t('common.home'), path: withLang(lang, '/') },
          { name: t('case.worksBreadcrumb'), path: withLang(lang, '/raboty') },
          { name: title, path: pathname },
        ]}
      />

      {/* Шапка */}
      <section className="section-hero-bg" style={{ padding: '64px 0' }}>
        <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            <Link to={withLang(lang, '/')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('common.home')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <Link to={withLang(lang, '/raboty')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('case.worksBreadcrumb')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <span style={{ color: '#93A3AE' }}>{title}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', background: '#189BD5', color: '#FFFFFF', padding: '6px 12px', borderRadius: 3 }}>{category}</span>
            <span style={{ fontSize: 12, color: '#93A3AE', letterSpacing: '.06em' }}>{year}</span>
          </div>
          <h1 className="h1-page" style={{ margin: 0, fontSize: 44, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>{title}</h1>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: '#93A3AE', maxWidth: 640 }}>{subtitle}</p>
        </div>
      </section>

      {/* Главное фото + данные */}
      <section style={{ padding: '56px 0 0' }}>
        <div className="container-page grid-case-split" style={{ alignItems: 'stretch' }}>
          <div style={{ border: '1px solid #D9DEE2', borderRadius: 3, overflow: 'hidden', height: 460 }}>
            {mainPhoto
              ? <img src={mainPhoto} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <ImagePlaceholder height={460} radius={0} />}
          </div>
          <div style={{ background: '#FFFFFF', border: '1px solid #D9DEE2', borderRadius: 3, padding: 28, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#93A3AE', paddingBottom: 16 }}>{t('case.projectData')}</div>
            {facts.map((f, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '13px 0', borderTop: '1px solid #E7EBEE' }}>
                <span style={{ fontSize: 13, color: '#5C6670', flexShrink: 0 }}>{f.k}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#171C22', textAlign: 'right' }}>{f.v}</span>
              </div>
            ))}
            <Link
              to={withLang(lang, '/zayavka')}
              style={{ marginTop: 'auto', paddingTop: 24, textAlign: 'center', background: '#171C22', color: '#FFFFFF', fontSize: 12, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '14px 24px', borderRadius: 3, display: 'block', transition: 'background .15s' }}
            >
              {t('case.similarTaskCta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Опциональные блоки */}
      {blocks.map(renderBlock)}

      {/* Отступ перед каруселью */}
      <div style={{ height: 72 }} />

      {/* Похожие кейсы */}
      <section style={{ background: '#FFFFFF', borderTop: '1px solid #D9DEE2', padding: '64px 0 72px' }}>
        <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#189BD5' }}>{category}</span>
              <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('case.similarProjects')}</h2>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => scrollRail(-1)} aria-label={t('case.prevAria')} style={{ width: 44, height: 44, border: '1px solid #C6CDD3', background: '#FFFFFF', borderRadius: 3, cursor: 'pointer', fontSize: 18, color: '#171C22', transition: 'border-color .15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
              <button onClick={() => scrollRail(1)} aria-label={t('case.nextAria')} style={{ width: 44, height: 44, border: '1px solid #C6CDD3', background: '#FFFFFF', borderRadius: 3, cursor: 'pointer', fontSize: 18, color: '#171C22', transition: 'border-color .15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>→</button>
            </div>
          </div>

          <div
            ref={railRef}
            className="case-rail"
            style={{
              display: 'flex', gap: 16, overflowX: 'auto',
              scrollSnapType: 'x mandatory', scrollbarWidth: 'none',
              margin: '0 -32px', padding: '0 32px 4px 32px',
            }}
          >
            {similar.map((s, i) => (
              <Link
                key={i}
                to={withLang(lang, '/raboty')}
                style={{
                  flex: '0 0 320px', scrollSnapAlign: 'start',
                  background: '#EEF1F3', border: '1px solid #D9DEE2', borderRadius: 3,
                  overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  transition: 'border-color .15s,transform .15s',
                }}
              >
                <ImagePlaceholder height={180} radius={0} />
                <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: '#189BD5' }}>{s.category}</span>
                    <span style={{ fontSize: 11, color: '#93A3AE', letterSpacing: '.06em' }}>{s.year}</span>
                  </div>
                  <span style={{ fontSize: 15.5, fontWeight: 650 as number, color: '#171C22' }}>{s.title}</span>
                  <span style={{ fontSize: 12.5, lineHeight: 1.55, color: '#5C6670' }}>{s.desc}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: '#171C22', padding: '64px 0' }}>
        <div className="container-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#FFFFFF' }}>{t('case.ctaTitle')}</h2>
            <p style={{ margin: 0, fontSize: 15, color: '#93A3AE' }}>{t('case.ctaDesc')}</p>
          </div>
          <Link to={withLang(lang, '/zayavka')} style={{ flexShrink: 0, background: '#189BD5', color: '#FFFFFF', fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '16px 32px', borderRadius: 3, transition: 'background .15s' }}>
            {t('common.submitApplication')}
          </Link>
        </div>
      </section>

      <Footer />
    </>
  )
}
